// src/common/utils/error-utils.js
/**
 * Extract a meaningful error message from various error types.
 * Handles Error objects, HTTP response errors, and nested error structures.
 */
export function extractErrorMessage(error) {
  if (!error) return 'Unknown error occurred';
  
  // If it's already a string, return it
  if (typeof error === 'string') return error;
  
  // Check common error message properties
  if (error.message && typeof error.message === 'string') return error.message;
  if (error.error && typeof error.error === 'string') return error.error;
  if (error.detail && typeof error.detail === 'string') return error.detail;
  
  // Handle nested error structures (common in API responses)
  if (error.error && typeof error.error === 'object') {
    const nested = extractErrorMessage(error.error);
    if (nested !== 'Unknown error occurred') return nested;
  }
  
  if (error.detail && typeof error.detail === 'object') {
    const nested = extractErrorMessage(error.detail);
    if (nested !== 'Unknown error occurred') return nested;
  }
  
  // Handle arrays of errors (validation errors)
  if (Array.isArray(error)) {
    const messages = error.map(e => extractErrorMessage(e)).filter(m => m !== 'Unknown error occurred');
    if (messages.length > 0) return messages.join('; ');
  }
  
  // Handle validation error arrays
  if (error.detail && Array.isArray(error.detail)) {
    const messages = error.detail.map(e => {
      if (typeof e === 'string') return e;
      if (e.msg && typeof e.msg === 'string') return e.msg;
      if (e.message && typeof e.message === 'string') return e.message;
      if (typeof e === 'object') {
        // For Pydantic validation errors, try to extract meaningful info
        const loc = e.loc ? `[${e.loc.join('.')}]` : '';
        const msg = e.msg || e.message || '';
        return loc && msg ? `${loc} ${msg}` : (msg || extractErrorMessage(e));
      }
      return extractErrorMessage(e);
    }).filter(m => m && m !== 'Unknown error occurred');
    if (messages.length > 0) return messages.join('; ');
  }
  
  // Last resort: try JSON.stringify for objects, but make it readable
  try {
    if (typeof error === 'object' && error.constructor === Object) {
      const str = JSON.stringify(error);
      if (str && str !== '{}' && str !== 'null') {
        return str.length > 200 ? str.substring(0, 197) + '...' : str;
      }
    }
  } catch (e) {
    // JSON.stringify failed, fall through
  }
  
  // Final fallback
  return error.toString?.() || 'Unknown error occurred';
}

